import { useEffect } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';

const statusOptions = [
  { value: 'ACTIVE', text: 'Active' },
  { value: 'INACTIVE', text: 'Inactive' },
];

const CompanyModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      company: '',
      division: '',
      description: '',
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (isOpen && defaultValues) {
      reset({
        company: defaultValues.company || '',
        division: defaultValues.division || '',
        description: defaultValues.description || '',
        status: defaultValues.status || 'ACTIVE',
      });
    }
  }, [isOpen, defaultValues, reset]);

  const handleClose = () => {
    reset({ company: '', division: '', description: '', status: 'ACTIVE' });
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Title + Close */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              {isEditMode
                ? `Edit > ${defaultValues?.company || 'Company'}`
                : 'Add Company'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form className="flex flex-col gap-5 text-text-hint" onSubmit={handleSubmit(onSubmit)}>
            {/* Company Code */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="company" className="text-text-hint mb-1">
                Company Code <span className="text-danger-500">*</span>
              </label>
              <input
                {...register('company', {
                  required: 'Company Code is required',
                  maxLength: { value: 20, message: 'Max 20 characters' },
                })}
                type="text"
                placeholder="Company Code"
                id="company"
                disabled={isEditMode}
                className={`outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black ${
                  isEditMode ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                }`}
              />
              {errors.company && (
                <span className="text-red-500 text-xs">{errors.company.message}</span>
              )}
            </div>

            {/* Division + Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1">
                <label htmlFor="division" className="text-text-hint mb-1">
                  Division
                </label>
                <input
                  {...register('division', {
                    maxLength: { value: 20, message: 'Max 20 characters' },
                  })}
                  type="text"
                  placeholder="Division"
                  id="division"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.division && (
                  <span className="text-red-500 text-xs">{errors.division.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="description" className="text-text-hint mb-1">
                  Description
                </label>
                <input
                  {...register('description', {
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Description"
                  id="description"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.description && (
                  <span className="text-red-500 text-xs">{errors.description.message}</span>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint mb-1">Status</label>
              <ul className="grid grid-cols-2 w-48">
                {statusOptions.map((option, index) => (
                  <li key={option.value} className="relative">
                    <input
                      {...register('status')}
                      className="sr-only peer"
                      type="radio"
                      value={option.value}
                      id={`company_status_${option.value}`}
                    />
                    <label
                      className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 text-sm ${
                        index === 0
                          ? 'rounded-tl-full rounded-bl-full'
                          : 'rounded-tr-full rounded-br-full'
                      }`}
                      htmlFor={`company_status_${option.value}`}
                    >
                      {option.text}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-row-reverse justify-between items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? isEditMode ? 'Updating...' : 'Saving...'
                  : isEditMode ? 'Update' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default CompanyModal;
